import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Users, Plus, MessageCircle, Heart, Eye, Clock, 
  Loader2, ChevronRight, Send, ArrowLeft, Trash2, Award, TrendingUp
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

const Forum = () => {
  const { token, user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [posts, setPosts] = useState([]);
  const [featuredPosts, setFeaturedPosts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [commenting, setCommenting] = useState(false);

  // New post form
  const [newTitle, setNewTitle] = useState("");
  const [newContent, setNewContent] = useState("");
  const [newCategory, setNewCategory] = useState("");

  // Comment form
  const [commentText, setCommentText] = useState("");

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchCategories();
    fetchFeaturedPosts();
    fetchPosts();
  }, [selectedCategory]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/forum/categories`);
      setCategories(res.data.categories);
    } catch (error) {
      // data loads with fallback state
    }
  };

  const fetchFeaturedPosts = async () => {
    try {
      const res = await axios.get(`${API}/forum/featured`);
      setFeaturedPosts(res.data.featured_posts);
    } catch (error) {
      // data loads with fallback state
    }
  };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const url = selectedCategory 
        ? `${API}/forum/posts?category=${selectedCategory}`
        : `${API}/forum/posts`;
      const res = await axios.get(url);
      setPosts(res.data.posts);
    } catch (error) {
      // data loads with fallback state
    } finally {
      setLoading(false);
    }
  };

  const fetchPost = async (postId) => {
    try {
      const res = await axios.get(`${API}/forum/posts/${postId}`);
      setSelectedPost(res.data);
    } catch (error) {
      // data loads with fallback state
    }
  };

  const handleCreatePost = async () => {
    if (!newTitle.trim() || !newContent.trim() || !newCategory) {
      toast.error("Please fill in all fields");
      return;
    }

    setCreating(true);
    try {
      await axios.post(`${API}/forum/posts`, {
        title: newTitle,
        content: newContent,
        category: newCategory,
        tags: []
      }, { headers, withCredentials: true });

      toast.success("Post created! ✨");
      setCreateDialogOpen(false);
      setNewTitle("");
      setNewContent("");
      setNewCategory("");
      fetchPosts();
    } catch (error) {
      toast.error("Failed to create post");
    } finally {
      setCreating(false);
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) return;

    setCommenting(true);
    try {
      await axios.post(`${API}/forum/posts/${selectedPost.post_id}/comments`, {
        content: commentText
      }, { headers, withCredentials: true });

      toast.success("Comment added!");
      setCommentText("");
      fetchPost(selectedPost.post_id);
    } catch (error) {
      toast.error("Failed to add comment");
    } finally {
      setCommenting(false);
    }
  };

  const handleLikePost = async (postId) => {
    try {
      const res = await axios.post(`${API}/forum/posts/${postId}/like`, {}, { headers, withCredentials: true });
      
      if (selectedPost?.post_id === postId) {
        fetchPost(postId);
      } else {
        fetchPosts();
      }
    } catch (error) {
      toast.error("Please login to like posts");
    }
  };

  const getCategoryInfo = (categoryId) => {
    return categories.find(c => c.id === categoryId) || { name: categoryId, icon: "💬" };
  };

  // Post Detail View
  if (selectedPost) {
    return (
      <div className="space-y-6" data-testid="forum-post-detail">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Button
            variant="ghost"
            onClick={() => setSelectedPost(null)}
            className="text-mystic-300 hover:text-white mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Forum
          </Button>

          <Card className="glass">
            <CardContent className="py-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <Badge className="mb-2" variant="outline">
                    {getCategoryInfo(selectedPost.category).icon} {getCategoryInfo(selectedPost.category).name}
                  </Badge>
                  <h1 className="text-2xl font-bold text-white">{selectedPost.title}</h1>
                  <div className="flex items-center gap-4 mt-2 text-mystic-400 text-sm">
                    <span>by {selectedPost.author_name}</span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDistanceToNow(new Date(selectedPost.created_at), { addSuffix: true })}
                    </span>
                    <span className="flex items-center gap-1">
                      <Eye className="w-3 h-3" />
                      {selectedPost.views}
                    </span>
                  </div>
                </div>
              </div>

              <div className="text-mystic-100 whitespace-pre-wrap mb-6">
                {selectedPost.content}
              </div>

              <div className="flex items-center gap-4 pt-4 border-t border-white/10">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleLikePost(selectedPost.post_id)}
                  className={`text-mystic-300 hover:text-pink-400 ${
                    selectedPost.likes?.includes(user?.user_id) ? "text-pink-400" : ""
                  }`}
                >
                  <Heart className={`w-4 h-4 mr-1 ${
                    selectedPost.likes?.includes(user?.user_id) ? "fill-current" : ""
                  }`} />
                  {selectedPost.likes?.length || 0}
                </Button>
                <span className="text-mystic-400 text-sm flex items-center gap-1">
                  <MessageCircle className="w-4 h-4" />
                  {selectedPost.comments?.length || 0} comments
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Comments Section */}
          <Card className="glass mt-6">
            <CardHeader>
              <CardTitle className="text-white">Comments</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add Comment */}
              {token && (
                <div className="flex gap-3 pb-4 border-b border-white/10">
                  <Textarea
                    placeholder="Share your thoughts..."
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    className="bg-mystic-900/50 border-white/10 text-white placeholder:text-white/30 min-h-[80px]"
                  />
                  <Button
                    onClick={handleAddComment}
                    disabled={commenting || !commentText.trim()}
                    className="bg-mystic-500 hover:bg-mystic-400 text-white"
                  >
                    {commenting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              )}

              {/* Comments List */}
              {selectedPost.comments?.length > 0 ? (
                <div className="space-y-4">
                  {selectedPost.comments.map((comment, idx) => (
                    <motion.div
                      key={comment.comment_id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className="glass-light rounded-lg p-4"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-mystic-300 font-medium">{comment.author_name}</span>
                        <span className="text-mystic-500 text-xs">
                          {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                        </span>
                      </div>
                      <p className="text-mystic-100">{comment.content}</p>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <p className="text-mystic-400 text-center py-8">No comments yet. Be the first to share!</p>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Forum List View
  return (
    <div className="space-y-6" data-testid="forum-page">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
              <Users className="w-8 h-8 text-pink-400" />
              Sacred Healing Together Forum
            </h1>
            <p className="text-mystic-200/70">
              A safe space for sharing, healing, and supporting each other
            </p>
          </div>
          {token && (
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-mystic-500 hover:bg-mystic-400 text-white rounded-full">
                  <Plus className="w-4 h-4 mr-2" />
                  New Post
                </Button>
              </DialogTrigger>
              <DialogContent className="glass border-white/10 text-white max-w-lg">
                <DialogHeader>
                  <DialogTitle className="text-white">Create New Post</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label className="text-mystic-100">Category</Label>
                    <Select value={newCategory} onValueChange={setNewCategory}>
                      <SelectTrigger className="bg-mystic-900/50 border-white/10 text-white">
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                      <SelectContent className="bg-mystic-800 border-white/10">
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id} className="text-white">
                            {cat.icon} {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-mystic-100">Title</Label>
                    <Input
                      value={newTitle}
                      onChange={(e) => setNewTitle(e.target.value)}
                      placeholder="What's on your mind?"
                      className="bg-mystic-900/50 border-white/10 text-white placeholder:text-white/30"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-mystic-100">Content</Label>
                    <Textarea
                      value={newContent}
                      onChange={(e) => setNewContent(e.target.value)}
                      placeholder="Share your thoughts, experiences, or questions..."
                      className="bg-mystic-900/50 border-white/10 text-white placeholder:text-white/30 min-h-[150px]"
                    />
                  </div>
                  <Button
                    onClick={handleCreatePost}
                    disabled={creating}
                    className="w-full bg-mystic-500 hover:bg-mystic-400 text-white rounded-full"
                  >
                    {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Create Post
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </motion.div>

      {/* Categories */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div className="flex flex-wrap gap-2">
          <Button
            variant={selectedCategory === null ? "default" : "outline"}
            size="sm"
            onClick={() => setSelectedCategory(null)}
            className={selectedCategory === null 
              ? "bg-mystic-500 text-white" 
              : "border-mystic-500/50 text-mystic-300 hover:bg-mystic-500/20"
            }
          >
            All Posts
          </Button>
          {categories.map((cat) => (
            <Button
              key={cat.id}
              variant={selectedCategory === cat.id ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedCategory(cat.id)}
              className={selectedCategory === cat.id 
                ? "bg-mystic-500 text-white" 
                : "border-mystic-500/50 text-mystic-300 hover:bg-mystic-500/20"
              }
            >
              {cat.icon} {cat.name}
            </Button>
          ))}
        </div>
      </motion.div>

      {/* Featured Posts Section */}
      {featuredPosts.length > 0 && !selectedCategory && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          <Card className="glass bg-gradient-to-br from-amber-500/10 to-purple-500/10 border-amber-500/20">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-400" />
                Featured Posts
                <Badge variant="outline" className="ml-2 border-amber-500/30 text-amber-300 text-xs">
                  Most Engaged
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {featuredPosts.map((post, idx) => (
                  <motion.div
                    key={post.post_id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: idx * 0.1 }}
                    className="glass-light rounded-xl p-4 cursor-pointer hover:border-amber-500/30 transition-all border border-transparent"
                    onClick={() => fetchPost(post.post_id)}
                    data-testid={`featured-post-${idx}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline" className="text-xs border-mystic-500/30 text-mystic-300">
                        {getCategoryInfo(post.category).icon} {getCategoryInfo(post.category).name}
                      </Badge>
                      {idx === 0 && (
                        <Badge className="bg-amber-500/20 text-amber-300 text-xs">
                          <TrendingUp className="w-3 h-3 mr-1" />
                          Top
                        </Badge>
                      )}
                    </div>
                    <h4 className="text-white font-medium mb-1 line-clamp-1">{post.title}</h4>
                    <p className="text-mystic-200/60 text-sm line-clamp-2 mb-3">{post.content}</p>
                    <div className="flex items-center gap-3 text-mystic-400 text-xs">
                      <span className="flex items-center gap-1">
                        <Heart className="w-3 h-3" />
                        {post.likes?.length || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <MessageCircle className="w-3 h-3" />
                        {post.comments_count || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        {post.views || 0}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Posts List */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-mystic-300" />
          </div>
        ) : posts.length === 0 ? (
          <Card className="glass">
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 text-mystic-500 mx-auto mb-4" />
              <h3 className="text-xl text-white mb-2">No Posts Yet</h3>
              <p className="text-mystic-200/70 mb-4">
                Be the first to start a conversation in this sacred space.
              </p>
              {token && (
                <Button
                  onClick={() => setCreateDialogOpen(true)}
                  className="bg-mystic-500 hover:bg-mystic-400 text-white rounded-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Post
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {posts.map((post, idx) => (
              <motion.div
                key={post.post_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                <Card 
                  className="glass cursor-pointer hover:border-mystic-300/30 transition-all"
                  onClick={() => fetchPost(post.post_id)}
                >
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline" className="text-xs">
                            {getCategoryInfo(post.category).icon} {getCategoryInfo(post.category).name}
                          </Badge>
                        </div>
                        <h3 className="text-lg font-semibold text-white mb-1">{post.title}</h3>
                        <p className="text-mystic-200/70 text-sm line-clamp-2">{post.content}</p>
                        
                        <div className="flex items-center gap-4 mt-3 text-mystic-400 text-sm">
                          <span>by {post.author_name}</span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
                          </span>
                          <span className="flex items-center gap-1">
                            <Heart className="w-3 h-3" />
                            {post.likes?.length || 0}
                          </span>
                          <span className="flex items-center gap-1">
                            <MessageCircle className="w-3 h-3" />
                            {post.comments_count || 0}
                          </span>
                          <span className="flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {post.views || 0}
                          </span>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-mystic-500 flex-shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default Forum;
